// Translation Dictionary
// Supports English (en) and Burmese (mm)

export type Language = 'en' | 'mm';

export const TRANSLATIONS = {
    en: {
        // Header
        editionInfo: 'brought to you by tastelessmediabuyer.vip',
        paperTitleMm: 'ပြည်တွင်းနေ့စဉ်ဈေးနှုန်းများ',
        paperTitle: 'Myanmar Daily Prices',
        volumeLine: 'Price Information Gazette',
        subtitle: 'ပြည်တွင်းနေ့စဉ်ဈေးနှုန်းများ',
        loading: 'Loading...',
        updating: 'Updating...',
        shoppersLedger: "Shopper's Ledger",

        // Sections
        headlines: "Today's Headlines",
        marketPrices: "Market Prices",

        // UI Elements
        addToCart: "+ Add to List",
        addedToCart: "✔ Added",
        sponsored: "Sponsored",
        advertisement: "ADVERTISEMENT",
        adPlaceholder: "Your advertisement could be here",

        // Status
        trendUp: "Up",
        trendDown: "Down",
        trendStable: "Stable",

        // Footer
        sources: "Data Sources: Denko • Central Statistical Organization • Citymall",
        copyright: "© 2026 Myanmar Daily Prices. All prices for reference only. Not financial advice.",

        // Ledger
        ledgerTitle: "Shopper's Ledger",
        emptyLedger: "Your ledger is empty",
        emptyHint: "Add items from the market listings",
        estimatedTotal: "Estimated Total",
        clearLedger: "Clear Ledger",
        close: "Close"
    },
    mm: {
        // Header
        editionInfo: 'tastelessmediabuyer.vip မှ ပူးပေါင်းတင်ဆက်သည်',
        paperTitleMm: 'ပြည်တွင်းနေ့စဉ်ဈေးနှုန်းများ',
        paperTitle: 'Myanmar Daily Prices',
        volumeLine: 'ဈေးနှုန်းသတင်းအချက်အလက်',
        subtitle: 'Myanmar Daily Prices',
        loading: 'ခေတ္တစောင့်ပါ...',
        updating: 'မွမ်းမံနေသည်...',
        shoppersLedger: "ဈေးဝယ်စာရင်း",

        // Sections
        headlines: "ယနေ့ခေါင်းကြီးပိုင်း",
        marketPrices: "ဈေးကွက်ပေါက်ဈေးများ",

        // UI Elements
        addToCart: "+ စာရင်းသွင်းမည်",
        addedToCart: "✔ ထည့်ပြီးပါပြီ",
        sponsored: "ပံ့ပိုးမှု",
        advertisement: "ကြော်ငြာ",
        adPlaceholder: "ဤနေရာတွင် ကြော်ငြာထည့်သွင်းနိုင်သည်",

        // Status
        trendUp: "တက်",
        trendDown: "ကျ",
        trendStable: "ငြိမ်",

        // Footer
        sources: "မှီငြမ်း: Denko • ဗဟိုစာရင်းအင်းအဖွဲ့ • Citymall",
        copyright: "© ၂၀၂၆ ပြည်တွင်းနေ့စဉ်ဈေးနှုန်းများ။ ရည်ညွှန်းရန်သာ။ ငွေကြေးအကြံဉာဏ်မဟုတ်ပါ။",

        // Ledger
        ledgerTitle: "ဈေးဝယ်စာရင်း",
        emptyLedger: "စာရင်းဗလာဖြစ်နေသည်",
        emptyHint: "ဈေးနှုန်းများမှ ပစ္စည်းထည့်ပါ",
        estimatedTotal: "ခန့်မှန်းကုန်ကျငွေ",
        clearLedger: "စာရင်းရှင်းမည်",
        close: "ပိတ်မည်"
    }
};

export function t(key: keyof typeof TRANSLATIONS.en, lang: Language): string {
    return TRANSLATIONS[lang][key] || TRANSLATIONS.en[key];
}

/**
 * Format date in newspaper style (English or Burmese)
 */
export function formatNewspaperDate(date: Date = new Date(), lang: Language = 'en'): string {
    if (lang === 'mm') {
        // Burmese Date Format
        const months = [
            'ဇန်နဝါရီ', 'ဖေဖော်ဝါရီ', 'မတ်', 'ဧပြီ', 'မေ', 'ဇွန်',
            'ဇူလိုင်', 'သြဂုတ်', 'စက်တင်ဘာ', 'အောက်တိုဘာ', 'နိုဝင်ဘာ', 'ဒီဇင်ဘာ'
        ];

        const days = [
            'တနင်္ဂနွေ', 'တနင်္လာ', 'အင်္ဂါ', 'ဗုဒ္ဓဟူး', 'ကြာသပတေး', 'သောကြာ', 'စနေ'
        ];

        const dayName = days[date.getDay()];
        const monthName = months[date.getMonth()];
        const year = date.getFullYear();
        // Convert numbers to Burmese digits
        const day = date.getDate().toString().replace(/\d/g, d => "၀၁၂၃၄၅၆၇၈၉"[parseInt(d)]);
        const yearMm = year.toString().replace(/\d/g, d => "၀၁၂၃၄၅၆၇၈၉"[parseInt(d)]);

        return `${yearMm} ခုနှစ်၊ ${monthName}လ (${day}) ရက်၊ ${dayName}နေ့`;
    } else {
        // English Date Format
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

        const dayName = days[date.getDay()];
        const monthName = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();

        const suffix = (day === 1 || day === 21 || day === 31) ? 'st'
            : (day === 2 || day === 22) ? 'nd'
                : (day === 3 || day === 23) ? 'rd'
                    : 'th';

        return `${dayName}, ${monthName} ${day}${suffix}, ${year}`;
    }
}
